import React from "react";
import i18next, { t as T } from "i18next";
import { Link } from "react-router-dom";
import {
  ArrowRight, Trophy, Award, Users, MessageCircle, MessageSquare,
  Star, Megaphone, Flame, BookOpen, BadgeCheck,
} from "lucide-react";

/**
 * PromoSpot — fargerike reklamekort injisert i feed hver 5. post.
 * Varianter:
 *  1) `creator_promo` — lilla banner + 4 creator-kort (siste aktive skapere)
 *  2) `cards`-array (groups + novels) — mini-kort-grid i spoten
 *  3) Kompakt variant (felleschat, forum_cta, forum_hot) — én linje
 *  4) Contest-grid (contest_active, contest_winners) — bilder i grid
 *  5) Standard banner (fallback)
 */

const ACCENT = {
  magenta: { grad: "linear-gradient(135deg,#ff2b8a 0%,#8b1d7a 60%,#4a0d5c 100%)", badge: "bg-pink-500 text-white", text: "text-pink-50", ring: "ring-pink-400/40", cta: "bg-pink-500 hover:bg-pink-400 text-white" },
  gold:    { grad: "linear-gradient(135deg,#ffd145 0%,#ff8b1e 55%,#c54a00 100%)", badge: "bg-amber-500 text-amber-950", text: "text-amber-50", ring: "ring-amber-300/40", cta: "bg-amber-400 hover:bg-amber-300 text-amber-950" },
  cyan:    { grad: "linear-gradient(135deg,#00e0ff 0%,#008fd1 60%,#004a7a 100%)", badge: "bg-cyan-400 text-cyan-950", text: "text-cyan-50", ring: "ring-cyan-300/40", cta: "bg-cyan-400 hover:bg-cyan-300 text-cyan-950" },
  emerald: { grad: "linear-gradient(135deg,#2ee68a 0%,#0e8a5a 55%,#013d2a 100%)", badge: "bg-emerald-400 text-emerald-950", text: "text-emerald-50", ring: "ring-emerald-300/40", cta: "bg-emerald-400 hover:bg-emerald-300 text-emerald-950" },
  pink:    { grad: "linear-gradient(135deg,#ff5fa8 0%,#ff2d5e 55%,#8a0030 100%)", badge: "bg-pink-400 text-pink-950", text: "text-pink-50", ring: "ring-pink-300/40", cta: "bg-pink-400 hover:bg-pink-300 text-pink-950" },
  orange:  { grad: "linear-gradient(135deg,#ff9933 0%,#ff4b1f 60%,#8a1b00 100%)", badge: "bg-orange-400 text-orange-950", text: "text-orange-50", ring: "ring-orange-300/40", cta: "bg-orange-400 hover:bg-orange-300 text-orange-950" },
  violet:  { grad: "linear-gradient(135deg,#a67bff 0%,#6a2bd4 55%,#2a0a68 100%)", badge: "bg-violet-400 text-violet-950", text: "text-violet-50", ring: "ring-violet-300/40", cta: "bg-violet-400 hover:bg-violet-300 text-violet-950" },
  fuchsia: { grad: "linear-gradient(135deg,#ff4df0 0%,#a319b5 55%,#3d0046 100%)", badge: "bg-fuchsia-400 text-fuchsia-950", text: "text-fuchsia-50", ring: "ring-fuchsia-300/40", cta: "bg-fuchsia-400 hover:bg-fuchsia-300 text-fuchsia-950" },
};

const KIND_ICON = {
  contest_active: Trophy, contest_winners: Award,
  group_top_members: Flame, group_recent_active: Users,
  novels_recent: BookOpen, felleschat: MessageCircle,
  forum_hot: Flame, forum_cta: MessageSquare,
  creator_promo: Star,
};

function SpotHeader({ palette, status_label }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 bg-black/25 backdrop-blur-sm">
      <span className="font-meta text-[9px] uppercase tracking-[0.3em] text-white/90 inline-flex items-center gap-1.5">
        <Megaphone className="h-3 w-3" />
        {T("promo_spot.discover")}
      </span>
      {status_label && (
        <span className={`font-meta text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm ${palette.badge}`}>
          {status_label}
        </span>
      )}
    </div>
  );
}

// --- Novelle-kort (match /noveller-stil) ---
function NovelCard({ item }) {
  return (
    <Link
      to={item.href}
      onClick={(e) => e.stopPropagation()}
      className="block bg-black/55 rounded-md p-3 ring-1 ring-white/10 hover:ring-white/30 transition"
      data-testid={`novel-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-fraunces italic text-lg leading-tight text-[var(--cream)] line-clamp-2">
          {item.title}
        </h4>
        {item.is_free && (
          <span className="shrink-0 inline-flex items-center gap-1 font-meta text-[8px] uppercase tracking-widest text-[var(--gold)] bg-black/50 px-1.5 py-0.5 rounded">
            {T("promo_spot.free")}
          </span>
        )}
      </div>
      {item.excerpt && (
        <p className="text-xs text-[var(--cream-soft)]/85 font-manrope line-clamp-4 mb-2">
          {item.excerpt}…
        </p>
      )}
      <div className="flex items-center justify-between font-meta text-[9px] uppercase tracking-wider text-[var(--cream-faint)]">
        <span>
          {item.author_username && <>@{item.author_username}</>}
          {item.author_location && <> · {item.author_location}</>}
          {item.word_count > 0 && <> · {item.word_count} {T("promo_spot.words")}</>}
        </span>
        <span>{"★".repeat(Math.round(item.rating_avg || 0))}{"☆".repeat(5 - Math.round(item.rating_avg || 0))}</span>
      </div>
      <div className="flex items-center justify-between font-meta text-[9px] uppercase tracking-wider text-[var(--cream-faint)] mt-1">
        <span>♡ {item.like_count || 0}</span>
        <span>{item.created_at ? new Date(item.created_at).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
      </div>
    </Link>
  );
}

// --- Gruppe-kort (match /grupper-stil) ---
function GroupCard({ item }) {
  return (
    <Link
      to={item.href}
      onClick={(e) => e.stopPropagation()}
      className="block bg-black/55 rounded-md overflow-hidden ring-1 ring-white/10 hover:ring-white/30 transition flex flex-col"
      data-testid={`group-card-${item.id}`}
    >
      <div className="aspect-video bg-black/40 flex items-center justify-center overflow-hidden">
        {item.cover_url ? (
          <img src={item.cover_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <Users className="h-6 w-6 sm:h-10 sm:w-10 text-white/25" strokeWidth={1.4} />
        )}
      </div>
      <div className="p-2 sm:p-3 flex-1 flex flex-col">
        <h4 className="font-fraunces italic text-xs sm:text-sm text-[var(--cream)] line-clamp-1 mb-0.5 sm:mb-1">
          {item.name}
        </h4>
        {item.description && (
          <p className="hidden sm:block text-[11px] text-[var(--cream-soft)]/80 font-manrope line-clamp-2 mb-2 flex-1">
            {item.description}
          </p>
        )}
        <div className="flex items-center justify-between font-meta text-[8px] sm:text-[9px] uppercase tracking-widest text-[var(--cream-faint)] mt-auto gap-1">
          <span className="truncate">{item.member_count || 0} {T("promo_spot.members_short")}</span>
          <span className="shrink-0">
            {item.created_at ? new Date(item.created_at).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" }) : ""}
          </span>
        </div>
      </div>
    </Link>
  );
}

// --- Creator-kort (match /skapere-stil) ---
function CreatorCard({ c }) {
  return (
    <Link
      to={c.href}
      onClick={(e) => e.stopPropagation()}
      className="block relative overflow-hidden rounded-md transition-transform hover:scale-[1.02]"
      style={{
        background: "#141116",
        border: "2px solid rgba(255, 95, 168, 0.75)",
        boxShadow: "0 0 14px rgba(255, 95, 168, 0.2), inset 0 0 8px rgba(255, 95, 168, 0.1)",
      }}
      data-testid={`creator-card-${c.username}`}
    >
      <div className="absolute top-1 left-1 right-1 sm:top-1.5 sm:left-1.5 sm:right-1.5 z-10 flex items-center justify-between pointer-events-none">
        <span className="font-meta text-[7px] sm:text-[8px] uppercase tracking-widest bg-black/70 text-[var(--magenta)] px-1 sm:px-1.5 py-0.5 rounded">
          {T("promo_spot.creator")}
        </span>
        {c.is_featured && (
          <span className="hidden sm:inline-flex font-meta text-[8px] uppercase tracking-widest bg-black/70 text-[var(--gold)] px-1.5 py-0.5 rounded items-center">
            {T("promo_spot.featured")}
          </span>
        )}
      </div>
      <div className="aspect-[3/4] bg-black/60 overflow-hidden">
        {c.avatar_url ? (
          <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="font-fraunces italic text-3xl sm:text-5xl text-white/20">
              {(c.username || "?").slice(0, 2).toUpperCase()}
            </span>
          </div>
        )}
      </div>
      <div className="p-1.5 sm:p-2 bg-[#141116]">
        <div className="font-fraunces italic text-[11px] sm:text-xs text-[var(--cream)] truncate">
          {c.display_name}
        </div>
        <div className="hidden sm:flex items-center gap-1 mt-0.5 font-meta text-[9px] text-[var(--cream-faint)]">
          {c.is_verified && <BadgeCheck className="h-2.5 w-2.5 text-[var(--magenta)]" strokeWidth={2.4} />}
          <span>{c.follower_count || 0} {T("promo_spot.followers")}</span>
        </div>
        {c.region && (
          <div className="hidden sm:block font-meta text-[8px] uppercase tracking-wider text-[var(--cream-faint)] mt-0.5 truncate">
            🇳🇴 {c.region}
          </div>
        )}
      </div>
    </Link>
  );
}

const DATE_LOCALES = { no: "nb-NO", en: "en-GB", sv: "sv-SE", da: "da-DK" };
const dateLocale = () => DATE_LOCALES[(i18next.language || "no").slice(0, 2)] || "nb-NO";

// Oversetter faste spot-tekster fra backend; brukerinnhold (titler/navn) beholdes.
function localize(s) {
  const k = `promo_spot.${s.kind}`;
  const tr = (f, v, o) => (v ? T(`${k}.${f}`, { defaultValue: v, ...o }) : v);
  const dynTitle = s.kind === "contest_active" || s.kind === "forum_hot";
  let subtitle = s.kind === "contest_winners" ? s.subtitle : tr("subtitle", s.subtitle);
  let status = tr("status", s.status_label);
  if (s.kind === "contest_active") {
    status = s.status_key ? T(`${k}.status_${s.status_key}`, { defaultValue: s.status_label }) : s.status_label;
    if (s.entry_count != null) subtitle = T(`${k}.subtitle`, { type: T(`promo_spot.contest_type.${s.contest_type || "other"}`, { defaultValue: T("promo_spot.contest_type.other") }), n: s.entry_count });
  }
  if (s.kind === "forum_hot" && s.reply_count != null) subtitle = T(`${k}.subtitle`, { n: s.reply_count, user: s.author_username || "" });
  return { ...s, title: dynTitle ? s.title : tr("title", s.title), subtitle, cta: tr("cta", s.cta), status_label: status };
}

export default function PromoSpot({ spot: rawSpot }) {
  if (!rawSpot) return null;
  const spot = localize(rawSpot);
  const {
    kind, title, subtitle, image_url, images, cards, card_kind, creators,
    href, cta, accent = "magenta", status_label,
  } = spot;
  const Icon = KIND_ICON[kind] || Megaphone;
  const palette = ACCENT[accent] || ACCENT.magenta;

  // =========================================================
  // 1) creator_promo — lilla banner + 4 creator-kort
  // =========================================================
  if (kind === "creator_promo" && Array.isArray(creators) && creators.length > 0) {
    return (
      <section
        className={`block mb-4 rounded-md ring-1 ${palette.ring} shadow-lg overflow-hidden`}
        style={{ background: palette.grad }}
        data-testid={`beta-promo-spot-${kind}`}
      >
        <SpotHeader palette={palette} status_label={status_label} />
        <Link
          to={href || "#"}
          className="block px-4 pt-3 pb-2 hover:bg-black/10 transition"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className={`font-fraunces italic text-xl leading-tight ${palette.text}`}>{title}</h3>
              {subtitle && <p className={`text-xs mt-0.5 font-manrope ${palette.text} opacity-85`}>{subtitle}</p>}
            </div>
            {cta && (
              <span className={`shrink-0 font-meta text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm shadow-md ${palette.cta}`}>
                {cta}
              </span>
            )}
          </div>
        </Link>
        <div className="grid grid-cols-4 gap-1.5 p-2 sm:p-3 sm:gap-2 bg-black/15">
          {creators.map((c) => <CreatorCard key={c.username} c={c} />)}
        </div>
      </section>
    );
  }

  // =========================================================
  // 2) cards-array (groups + novels) — mini-kort-grid
  // =========================================================
  if (Array.isArray(cards) && cards.length > 0) {
    const gridCols = card_kind === "novel"
      ? "grid-cols-2"
      : "grid-cols-3";
    return (
      <section
        className={`block mb-4 rounded-md ring-1 ${palette.ring} shadow-lg overflow-hidden`}
        style={{ background: palette.grad }}
        data-testid={`beta-promo-spot-${kind}`}
      >
        <SpotHeader palette={palette} status_label={status_label} />
        <Link
          to={href || "#"}
          className="block px-4 pt-3 pb-2 hover:bg-black/10 transition"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className={`font-fraunces italic text-xl leading-tight ${palette.text}`}>{title}</h3>
              {subtitle && <p className={`text-xs mt-0.5 font-manrope ${palette.text} opacity-85`}>{subtitle}</p>}
            </div>
            {cta && (
              <span className={`shrink-0 font-meta text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm inline-flex items-center gap-1 shadow-md ${palette.cta}`}>
                {cta} <ArrowRight className="h-3 w-3" />
              </span>
            )}
          </div>
        </Link>
        <div className={`grid ${gridCols} gap-2 p-3 bg-black/15`}>
          {cards.map((item) => (
            card_kind === "novel"
              ? <NovelCard key={item.id} item={item} />
              : <GroupCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    );
  }

  // =========================================================
  // 3) Kompakt variant for ren tekst-spotter
  // =========================================================
  const COMPACT_KINDS = new Set(["felleschat", "forum_cta", "forum_hot"]);
  const imgList = Array.isArray(images) ? images.filter(Boolean) : [];
  const hasHero = !!image_url && imgList.length === 0;
  const hasGrid = imgList.length > 0;
  const isCompact = COMPACT_KINDS.has(kind) && !hasHero && !hasGrid;

  if (isCompact) {
    return (
      <Link
        to={href || "#"}
        className={`block mb-3 rounded-md ring-1 ${palette.ring} shadow hover:scale-[1.005] transition-transform overflow-hidden`}
        style={{ background: palette.grad }}
        data-testid={`beta-promo-spot-${kind}`}
      >
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="h-10 w-10 shrink-0 bg-black/25 rounded-sm flex items-center justify-center">
            <Icon className="h-5 w-5 text-white drop-shadow" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-meta text-[8px] uppercase tracking-[0.25em] text-white/70">{T("ui.discover_badge")}</span>
              {status_label && (
                <span className={`font-meta text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-sm ${palette.badge}`}>
                  {status_label}
                </span>
              )}
            </div>
            <h3 className={`font-fraunces italic text-sm leading-tight truncate ${palette.text}`}>{title}</h3>
            {subtitle && <p className={`text-[11px] line-clamp-1 font-manrope ${palette.text} opacity-85`}>{subtitle}</p>}
          </div>
          {cta && (
            <span className={`shrink-0 font-meta text-[9px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm shadow ${palette.cta}`}>
              {cta}
            </span>
          )}
        </div>
      </Link>
    );
  }

  // =========================================================
  // 4) Standard banner (contest-grid m.m.)
  // =========================================================
  const gridCols = hasGrid
    ? imgList.length >= 6 ? "grid-cols-3" : imgList.length === 4 ? "grid-cols-4" : "grid-cols-3"
    : "";

  return (
    <Link
      to={href || "#"}
      className={`block mb-4 overflow-hidden rounded-md ring-1 ${palette.ring} shadow-lg hover:scale-[1.01] transition-transform`}
      style={{ background: palette.grad }}
      data-testid={`beta-promo-spot-${kind}`}
    >
      <SpotHeader palette={palette} status_label={status_label} />
      <div className="p-4">
        {hasHero && (
          <div className="relative aspect-[16/9] bg-black/30 overflow-hidden rounded-sm mb-3 ring-1 ring-white/10">
            <img src={image_url} alt={title || ""} className="w-full h-full object-cover" />
          </div>
        )}
        {hasGrid && (
          <div className={`grid gap-1 mb-3 ${gridCols}`}>
            {imgList.slice(0, imgList.length >= 6 ? 6 : 4).map((src, i) => (
              <div key={i} className="aspect-square bg-black/30 overflow-hidden rounded-sm ring-1 ring-white/10">
                {src ? <img src={src} alt="" className="w-full h-full object-cover" />
                     : <div className="w-full h-full flex items-center justify-center text-white/40"><Icon className="h-5 w-5" /></div>}
              </div>
            ))}
          </div>
        )}
        {!hasHero && !hasGrid && (
          <div className="flex items-center justify-center aspect-[16/7] mb-3 bg-black/15 rounded-sm ring-1 ring-white/10">
            <Icon className="h-16 w-16 text-white/90 drop-shadow-lg" strokeWidth={1.5} />
          </div>
        )}
        <div>
          <h3 className={`font-fraunces italic text-xl leading-tight mb-1 drop-shadow ${palette.text}`}>{title}</h3>
          {subtitle && <p className={`text-xs line-clamp-2 font-manrope ${palette.text} opacity-90`}>{subtitle}</p>}
        </div>
        {cta && (
          <div className="mt-3">
            <span className={`font-meta text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 inline-flex items-center gap-1.5 rounded-sm shadow-md ${palette.cta}`}>
              {cta} <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
